
import React from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { UserRole, AgeGroup } from '@/types/user';
import { getUserPermissions } from '@/utils/rbac';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { UserCog } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PermissionGate from '@/components/rbac/PermissionGate';
import ProfileForm from '@/components/profile/ProfileForm';
import PlayerDashboard from './previews/PlayerDashboard';
import CoachDashboard from './previews/CoachDashboard';
import ParentDashboard from './previews/ParentDashboard';
import TeamOwnerDashboard from './TeamOwnerDashboard';
import VenueDashboard from './VenueDashboard';
import CreativeDashboard from './CreativeDashboard';
import SupportDashboard from './SupportDashboard';
import FanDashboard from './FanDashboard';
import WelcomeHeader from './WelcomeHeader';
import ProfileCompletionNotice from './ProfileCompletionNotice';
import UserPermissionsDebug from './UserPermissionsDebug';
import AgeBasedNotices from './AgeBasedNotices';
import DailyBonusNotification from './DailyBonusNotification';
import ChildConnectionCode from './ChildConnectionCode';

const RoleBasedDashboard: React.FC = () => {
  const { profile } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const navigate = useNavigate();

  // Determine user role and age group
  const userRole = (profile?.role || internalUser?.role || 'player') as UserRole;
  const userAgeGroup = (profile?.age_group || internalUser?.age_group || '18-plus') as AgeGroup;
  const username = profile?.first_name || profile?.username || internalUser?.username || 'User';
  const isInternalUser = !!internalUser && !profile;

  // Get user's permissions
  const permissions = getUserPermissions(userRole, userAgeGroup);

  // Check if profile is complete (basic check)
  const isProfileComplete = profile && (
    profile.first_name || profile.last_name || profile.username
  );

  const handleProfileNavigation = () => {
    navigate('/profile-form');
  };

  const renderRoleDashboard = () => {
    switch (userRole) {
      case 'coach':
        return <CoachDashboard />;
      case 'team-owner':
        return <TeamOwnerDashboard />;
      case 'venue':
        return <VenueDashboard />;
      case 'creative':
        return <CreativeDashboard />;
      case 'support':
        return <SupportDashboard />;
      case 'fan':
        return <FanDashboard />;
      case 'parent':
        return <ParentDashboard />;
      case 'player':
      default:
        return <PlayerDashboard />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <WelcomeHeader
        username={username}
        userRole={userRole}
        userAgeGroup={userAgeGroup}
        isInternalUser={isInternalUser}
        isProfileComplete={isProfileComplete}
        onProfileNavigation={handleProfileNavigation}
      />

      {/* Daily Bonus Notification */}
      <DailyBonusNotification />

      {/* Child Connection Code - Only for Under 13 Users */}
      {(isInternalUser || profile) && (
        <ChildConnectionCode 
          userId={profile?.id || internalUser?.id || ''} 
          userAgeGroup={userAgeGroup} 
        />
      )}

      {/* Profile Completion Notice - Only for Supabase users */}
      {!isInternalUser && profile && (
        <ProfileCompletionNotice
          userRole={userRole}
          profile={profile}
          onProfileNavigation={handleProfileNavigation}
        />
      )}

      {/* Team Owner Dashboard - Show FIRST before profile for team-owner role */}
      {userRole === 'team-owner' && (
        <PermissionGate 
          permission="view_dashboard"
          fallback={null}
        >
          <TeamOwnerDashboard />
        </PermissionGate>
      )}

      {/* Profile Section - Show for all users */}
      <Card className="bg-[#0d1117] border border-gray-800">
        <CardHeader>
          <CardTitle className="text-xl text-sports-green flex items-center gap-2">
            <UserCog className="h-6 w-6" />
            Profile Management
          </CardTitle>
          <p className="text-gray-400">
            {isInternalUser 
              ? `View and manage your internal ${userRole.replace('-', ' ')} account information`
              : `Manage your ${userRole.replace('-', ' ')} profile information and settings`
            }
          </p>
        </CardHeader>
        <CardContent>
          <ProfileForm />
        </CardContent>
      </Card>


      {/* Role-specific Dashboard - For non-team-owner roles */}
      {userRole !== 'team-owner' && (
        <PermissionGate 
          permission="view_dashboard"
          fallback={
            <div className="bg-red-900/20 border border-red-500 rounded-lg p-6 text-center">
              <h3 className="text-lg font-medium text-red-400 mb-2">Access Restricted</h3>
              <p className="text-red-300">
                Your account doesn't have permission to access the dashboard. 
                Please contact support if you believe this is an error.
              </p>
            </div>
          }
        >
          {renderRoleDashboard()}
        </PermissionGate>
      )}

      {/* Age-based Notices */}
      <AgeBasedNotices userAgeGroup={userAgeGroup} />
    </div>
  );
};

export default RoleBasedDashboard;
